import { useMemo, useState } from 'react';
import { BiChevronDown, BiChevronRight } from 'react-icons/bi';
import { IoLockClosed } from 'react-icons/io5';

import { Switch } from '@/components/Inputs';
import { getWidgetDef } from '@/utils/dashboard.registry';
import {
  CATEGORY_ICONS,
  type WidgetCategory,
  type WidgetId,
  type WidgetLayoutItem,
} from '@/utils/dashboard.types';

import s from '../DashboardSettingsPage.module.scss';

type WidgetCategoryListProps = {
  groupedWidgets: Record<WidgetCategory, WidgetLayoutItem[]>;
  onToggle: (id: string) => void;
  lockedIds?: Set<WidgetId>;
  searchQuery?: string;
};

const EMPTY_LOCKED_IDS: Set<WidgetId> = new Set();

function categoryTitle(category: WidgetCategory): string {
  let title = category
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  if (title === 'Vessel') title = 'Vessels';
  if (title === 'Media') title = 'Library';
  if (title === 'Service Provider') title = 'Service Providers';
  return title;
}

export function WidgetCategoryList({
  groupedWidgets,
  onToggle,
  lockedIds = EMPTY_LOCKED_IDS,
  searchQuery = '',
}: WidgetCategoryListProps) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const toggleCategory = (category: string) => {
    setCollapsed((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  const query = searchQuery.trim().toLowerCase();
  const isSearching = query.length > 0;

  const filteredEntries = useMemo(() => {
    const entries = Object.entries(groupedWidgets) as [WidgetCategory, WidgetLayoutItem[]][];
    if (!isSearching) return entries;
    return entries
      .map(([category, items]) => {
        const catMatch = categoryTitle(category).toLowerCase().includes(query);
        const filteredItems = items.filter((item) => {
          if (catMatch) return true;
          const def = getWidgetDef(item.id);
          return def ? def.label.toLowerCase().includes(query) : false;
        });
        return [category, filteredItems] as [WidgetCategory, WidgetLayoutItem[]];
      })
      .filter(([, items]) => items.length > 0);
  }, [groupedWidgets, isSearching, query]);

  if (isSearching && filteredEntries.length === 0) {
    return <div className={s.searchEmpty}>No widgets match &ldquo;{searchQuery}&rdquo;</div>;
  }

  return (
    <>
      {filteredEntries.map(([category, items]) => {
        const sortedItems = [...items].sort((a, b) => a.order - b.order);
        const icon = CATEGORY_ICONS[category];
        const isCollapsed = isSearching ? false : (collapsed[category] ?? false);
        const visibleCount = sortedItems.filter((item) => item.visible).length;
        const totalCount = sortedItems.length;

        const title = categoryTitle(category);

        return (
          <div key={category} className={s.categoryGroup}>
            <button
              type="button"
              className={s.categoryGroupHeader}
              onClick={() => toggleCategory(category)}
              aria-expanded={!isCollapsed}
            >
              <span className={s.categoryGroupChevron}>
                {isCollapsed ? <BiChevronRight /> : <BiChevronDown />}
              </span>
              <div className={s.categoryGroupIcon}>{icon}</div>
              <span className={s.categoryGroupLabel}>{title}</span>
              <span className={s.categoryGroupCount}>
                {visibleCount}/{totalCount}
              </span>
            </button>
            {!isCollapsed && (
              <div className={s.subWidgetsList}>
                {sortedItems.map((item) => {
                  const def = getWidgetDef(item.id);
                  if (!def) return null;
                  const isLocked = lockedIds.has(item.id);
                  return (
                    <div
                      key={item.id}
                      className={`${s.subWidgetRow} ${isLocked ? s.subWidgetRowLocked : ''}`}
                      onClick={isLocked ? undefined : () => onToggle(item.id)}
                      role={isLocked ? undefined : 'button'}
                      tabIndex={isLocked ? -1 : 0}
                      aria-disabled={isLocked || undefined}
                      onKeyDown={
                        isLocked
                          ? undefined
                          : (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onToggle(item.id);
                              }
                            }
                      }
                    >
                      <span className={s.subWidgetLabel}>{def.label}</span>
                      {isLocked ? (
                        <span className={s.subWidgetLockIcon} aria-label="Locked">
                          <IoLockClosed size={16} />
                        </span>
                      ) : (
                        <span onClick={(e) => e.stopPropagation()}>
                          <Switch checked={item.visible} onChange={() => onToggle(item.id)} />
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
